/* eslint-disable react/no-unstable-nested-components */
import { XMarkIcon } from '@heroicons/react/24/outline'
import dayjs from 'dayjs'
import * as _ from 'lodash-es'
import PropTypes from 'prop-types'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { TextInput } from '@/components/forms/TextInput'
import { AddIcon, DownloadIcon, MinusIcon, SearchIcon } from '@/components/icons'
import { DataTable, getSortProps } from '@/components/shared/DataTable'
import { FilterPopover } from '@/components/shared/FilterPopover'
import { RESOLVED_CHECKBOX_FILTERS } from '@/components/shared/FilterPopover/constants'
import { Button } from '@/components/ui/Button'
import useAbortControllers from '@/hooks/AbortControllers'
import useHoverItem from '@/hooks/HoverItem'
import {
  createRepairOrderExport,
  getActionsNeeded,
  getAdvisors,
  getRepairOrderExport,
  getRepairOrders,
  getTechnicians,
} from '@/services/dealer.service'
import { DealerFilterStoreContext } from '@/stores/DealerFilterStore'
import { NotificationStoreContext } from '@/stores/NotificationStore'
import { TaskStoreContext } from '@/stores/TaskStore'
import { UserStoreContext } from '@/stores/UserStore'
import { track } from '@/utils/analytics'
import {
  EXPORT_INITIATED,
  FILTER_APPLIED,
  FILTER_CLEARED,
  SEARCH_PERFORMED,
  SORT_CHANGED,
} from '@/utils/analytics.constants'
import { handlePagination, toast } from '@/utils/helpers'

import ExpandableRow from '../components/ExpandableRow'
import RepairOrderNumberCell from '../components/RepairOrderNumberCell'
import {
  DEFAULT_FILTERS,
  DEFAULT_RESOLVED_DATE_FILTERS,
  RESOLVED_COLUMNS,
  RESOLVED_DATE_RANGES,
} from '../constants'
import { configureUrlParams } from '../utils'

// Display labels for the active-filter summary chips.
const DROPDOWN_FILTER_LABELS = {
  actionNeeded: 'Action Needed',
  advisor: 'Advisor',
  technician: 'Technician',
}

// Human summary of a from/to date range for a chip.
const dateRangeSummary = (from, to) => {
  if (from && to) return `${dayjs(from).format('MMM D')} – ${dayjs(to).format('MMM D, YYYY')}`
  if (from) return `From ${dayjs(from).format('MMM D, YYYY')}`
  return `Until ${dayjs(to).format('MMM D, YYYY')}`
}

export const ResolvedROs = ({
  active,
  dealerId,
  dealer,
  dealerName,
  onCountChange,
  tableLayoutWatch,
}) => {
  const navigate = useNavigate()
  const dealerFilterStore = useContext(DealerFilterStoreContext)

  const [, setError] = useState(null)
  const [hasOpened, setHasOpened] = useState(false)
  const [loadingResolved, setLoadingResolved] = useState(true)
  const isSameDealer = dealerFilterStore.currentDealerId === dealerId
  const [resolvedRepairOrders, setResolvedRepairOrders] = useState([])
  const [resolvedCurrentPage, setResolvedCurrentPage] = useState(
    isSameDealer ? dealerFilterStore.resolvedCurrentPage : 1,
  )
  const [resolvedPerPage, setResolvedPerPage] = useState(
    isSameDealer ? dealerFilterStore.resolvedPerPage : 20,
  )
  const [resolvedTotalRows, setResolvedTotalRows] = useState()
  const [resolvedPages, setResolvedPages] = useState(null)
  const [resolvedSortedColumn, setResolvedSortedColumn] = useState(
    isSameDealer ? dealerFilterStore.resolvedSortedColumn : '-resolved_at',
  )
  const [loadingExport, setLoadingExport] = useState(false)

  // Resolved Search Filters
  const [resolvedSearchTerm, setResolvedSearchTerm] = useState('')
  const [resolvedSearch, setResolvedSearch] = useState(null)
  const { unreadNotificationsCount } = useContext(NotificationStoreContext)

  const initialResolvedFilters =
    dealerFilterStore.currentDealerId === dealerId
      ? dealerFilterStore.resolvedFilters
      : DEFAULT_FILTERS
  const [selectedResolvedFilters, setSelectedResolvedFilters] = useState(initialResolvedFilters)

  const initialResolvedDateFilters =
    dealerFilterStore.currentDealerId === dealerId
      ? dealerFilterStore.resolvedDateFilters
      : DEFAULT_RESOLVED_DATE_FILTERS
  const [resolvedDateFilters, setResolvedDateFilters] = useState(initialResolvedDateFilters)

  // Sync filter changes to store for persistence
  useEffect(() => {
    dealerFilterStore.setResolvedFilters(selectedResolvedFilters)
  }, [selectedResolvedFilters])
  useEffect(() => {
    dealerFilterStore.setResolvedDateFilters(resolvedDateFilters)
  }, [resolvedDateFilters])

  // Analytics tracking that runs *after* both filter slices settle. FilterPopover's
  // Reset button calls onFiltersChange then onDateChange in sequence; tracking
  // inline on either handler would read a stale value of the other slice and
  // mis-report a reset as FILTER_APPLIED. Skipping the initial mount mirrors the
  // pattern used elsewhere (e.g. isResolvedInitialMount).
  const isFilterAnalyticsInitialMount = useRef(true)
  useEffect(() => {
    if (isFilterAnalyticsInitialMount.current) {
      isFilterAnalyticsInitialMount.current = false
      return
    }
    const isClearing =
      _.isEqual(selectedResolvedFilters, DEFAULT_FILTERS) &&
      _.isEqual(resolvedDateFilters, DEFAULT_RESOLVED_DATE_FILTERS)
    if (isClearing) {
      track(FILTER_CLEARED, { context: 'dealer_detail_resolved' })
      return
    }
    const checkboxCount = Object.values(selectedResolvedFilters).filter((v) => {
      if (Array.isArray(v)) return v.length > 0
      return v === true
    }).length
    const dateCount = Object.values(resolvedDateFilters).filter(Boolean).length
    track(FILTER_APPLIED, {
      context: 'dealer_detail_resolved',
      filter_count: checkboxCount + dateCount,
    })
  }, [selectedResolvedFilters, resolvedDateFilters])
  useEffect(() => {
    dealerFilterStore.setResolvedSortedColumn(resolvedSortedColumn)
  }, [resolvedSortedColumn])
  useEffect(() => {
    dealerFilterStore.setResolvedCurrentPage(resolvedCurrentPage)
  }, [resolvedCurrentPage])
  useEffect(() => {
    dealerFilterStore.setResolvedPerPage(resolvedPerPage)
  }, [resolvedPerPage])
  const { createNewTask } = useContext(TaskStoreContext)
  const { isImpersonating } = useContext(UserStoreContext)

  const resolvedSearchInputRef = useRef()
  const isResolvedInitialMount = useRef(true)

  // Cancels superseded resolved-list/filter requests so the last one
  // *requested* wins, not the last one to *resolve* (pagination races).
  const { getSignal } = useAbortControllers()

  const handleErrors = (m) => toast(m, 'error')

  const BASE_URL = useMemo(
    () =>
      `/dealers/${dealerId}/repair-orders/?state=Resolved&expand=line_items,line_items.actions_needed,line_items.actions_needed.action`,
    [dealerId],
  )

  const getUpdatedRepairOrders = useCallback(
    async (url) => {
      const response = await getRepairOrders(
        url,
        setLoadingResolved,
        setError,
        () => {},
        getSignal('resolved-repair-orders'),
      )

      if (response) {
        setResolvedRepairOrders(response.results)
        setResolvedTotalRows(response.count)
        setResolvedPages({ next: response.next, previous: response.previous })
      }

      // Loading is owned by getRepairOrders: it clears on success/error and is
      // intentionally left set when a request is superseded (canceled), so the
      // newer in-flight request keeps the spinner up. Don't reset it here.
      return response
    },
    [getSignal],
  )

  const [actionsNeeded, setActionsNeeded] = useState({
    resolved: [],
  })

  const [advisors, setAdvisors] = useState({
    resolved: [],
  })

  const [technicians, setTechnicians] = useState({
    resolved: [],
  })

  useEffect(() => {
    if (!hasOpened) return

    const getUpdatedData = async () => {
      const [updatedActionsResolved, updatedAdvisorsResolved, updatedTechniciansResolved] =
        await Promise.all([
          getActionsNeeded(dealerId, true, () => {}, handleErrors, getSignal('resolved-actions')),
          getAdvisors(dealerId, true, () => {}, handleErrors, getSignal('resolved-advisors')),
          getTechnicians(
            dealerId,
            true,
            () => {},
            handleErrors,
            getSignal('resolved-technicians'),
          ),
        ])

      setActionsNeeded({
        resolved: _.map(updatedActionsResolved, (a) => ({ id: a.id, label: a.name })),
      })
      setAdvisors({
        resolved: _.map(updatedAdvisorsResolved, (a) => ({ id: a, label: a })),
      })
      setTechnicians({
        resolved: _.map(updatedTechniciansResolved, (t) => ({ id: t, label: t })),
      })
    }

    getUpdatedData()
  }, [hasOpened, dealerId])

  /**
   * Configure the URL Resolved Repair Order parameters based on the search term and filters.
   */
  const urlParams = useMemo(
    () => configureUrlParams(resolvedSearch, selectedResolvedFilters, resolvedDateFilters),
    [resolvedSearch, selectedResolvedFilters, resolvedDateFilters],
  )

  // Active filters summarized as removable chips, so the current scope is
  // visible even when the filter panel is closed.
  const activeChips = useMemo(() => {
    const chips = []
    Object.entries(selectedResolvedFilters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => {
          chips.push({
            id: `${key}:${v.id}`,
            text: `${DROPDOWN_FILTER_LABELS[key] || key}: ${v.label}`,
            remove: () =>
              setSelectedResolvedFilters((prev) => ({
                ...prev,
                [key]: prev[key].filter((x) => x.id !== v.id),
              })),
          })
        })
      } else if (value === true) {
        const checkbox = RESOLVED_CHECKBOX_FILTERS.find((c) => c.key === key)
        if (checkbox) {
          chips.push({
            id: key,
            text: checkbox.label,
            remove: () => setSelectedResolvedFilters((prev) => ({ ...prev, [key]: false })),
          })
        }
      }
    })
    RESOLVED_DATE_RANGES.forEach(({ label, from, to }) => {
      if (resolvedDateFilters[from] || resolvedDateFilters[to]) {
        chips.push({
          id: from,
          text: `${label}: ${dateRangeSummary(
            resolvedDateFilters[from],
            resolvedDateFilters[to],
          )}`,
          remove: () => setResolvedDateFilters((prev) => ({ ...prev, [from]: '', [to]: '' })),
        })
      }
    })
    return chips
  }, [selectedResolvedFilters, resolvedDateFilters])

  const { hoveredItemId, handleMouseEnter, handleMouseLeave } = useHoverItem()

  const updatedResolvedColumns = RESOLVED_COLUMNS.map((column) => {
    if (column.name === 'RO #') {
      return {
        ...column,
        cell: (row) => (
          <RepairOrderNumberCell
            row={row}
            hoveredRowId={hoveredItemId}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          />
        ),
      }
    }
    return column
  })

  /**
   * Debounces the resolved search input.
   */
  const searchResolvedRepairOrders = useCallback(
    _.debounce((term) => {
      setResolvedSearch(term)
    }, 500),
    [],
  )

  useEffect(() => {
    if (!hasOpened) return

    const page = isResolvedInitialMount.current ? resolvedCurrentPage : 1
    if (!isResolvedInitialMount.current) setResolvedCurrentPage(1)
    isResolvedInitialMount.current = false
    const params = urlParams === '' ? '' : `&${urlParams}`

    getUpdatedRepairOrders(
      `${BASE_URL}&limit=${resolvedPerPage}&page=${page}&order_by=${resolvedSortedColumn}${params}`,
    ).then((response) => {
      if (resolvedSearch && response) {
        track(SEARCH_PERFORMED, {
          context: 'dealer_detail_resolved',
          has_results: response.count > 0,
        })
      }
    })
  }, [
    urlParams,
    hasOpened,
    resolvedPerPage,
    resolvedSortedColumn,
    unreadNotificationsCount,
    getUpdatedRepairOrders,
    BASE_URL,
  ])

  // The list loads the first time its tab opens, not before: it holds every RO the
  // dealer ever resolved, so most visits never need it.
  useEffect(() => {
    if (active && !hasOpened) setHasOpened(true)
  }, [active])

  // The tab still shows a count before then, from a one-row page. Once the list has
  // loaded, its own count takes over, with the search and filters applied — the same as
  // the Draft and Outstanding List tabs.
  useEffect(() => {
    if (hasOpened) return
    getRepairOrders(
      `/dealers/${dealerId}/repair-orders/?state=Resolved&limit=1`,
      () => {},
      () => {},
      () => {},
      getSignal('resolved-count'),
    ).then((response) => {
      if (response) onCountChange(response.count)
    })
  }, [dealerId, hasOpened, unreadNotificationsCount])

  useEffect(() => {
    if (hasOpened && resolvedTotalRows !== undefined) onCountChange(resolvedTotalRows)
  }, [hasOpened, resolvedTotalRows])

  const resolvedExpandableRow = ({ data }) => <ExpandableRow data={data} />

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-x-4">
          <TextInput
            className="w-72 pl-10"
            data-testid="resolved-repair-search"
            icon={<SearchIcon className="stroke-charcoal ml-2 w-5" />}
            endIcon={
              resolvedSearchTerm ? (
                <button
                  type="button"
                  onClick={() => {
                    setResolvedSearchTerm('')
                    searchResolvedRepairOrders('')
                    resolvedSearchInputRef.current.value = ''
                  }}
                >
                  <XMarkIcon className="text-midnight h-5" aria-hidden="true" />
                </button>
              ) : null
            }
            onChange={(e) => {
              searchResolvedRepairOrders(e.target.value)
              setResolvedSearchTerm(e.target.value)
            }}
            placeholder="Search"
            value={resolvedSearchTerm}
            ref={resolvedSearchInputRef}
          />

          <FilterPopover
            filters={selectedResolvedFilters}
            onFiltersChange={setSelectedResolvedFilters}
            checkboxFilters={RESOLVED_CHECKBOX_FILTERS}
            dropdownOptions={{
              actionNeeded: actionsNeeded.resolved,
              advisor: advisors.resolved,
              technician: technicians.resolved,
            }}
            dateRangeFilters={RESOLVED_DATE_RANGES}
            dateValues={resolvedDateFilters}
            onDateChange={setResolvedDateFilters}
            anchor="top"
            testId="resolved-repair-orders-filter"
          />
        </div>
        <div className="flex flex-row gap-2">
          <Button
            className="px-1 py-2"
            dataTestId="export-resolved-repair-orders"
            disabled={loadingExport || isImpersonating}
            label={<DownloadIcon className="h-4 stroke-[#ffffff]" />}
            ariaLabel="Export Resolved Repair Orders"
            onClick={async () => {
              track(EXPORT_INITIATED, { type: 'ro', dealer_id: dealerId })
              const baseParams =
                '?state=Resolved&expand=line_items,line_items.actions_needed,line_items.actions_needed.action'
              const params = urlParams === '' ? baseParams : `${baseParams}&${urlParams}`
              const exportTask = await createRepairOrderExport(
                `/dealers/${dealerId}/repair-order-exports/${params}`,
                handleErrors,
                setLoadingExport,
              )

              if (exportTask) {
                createNewTask('Download', exportTask, {
                  label: 'Repair Orders',
                  filename: `${dealerName} - Resolved Repair Orders.xlsx`,
                  checkUpdatedTask: async () => {
                    const updatedTask = await getRepairOrderExport(
                      dealerId,
                      exportTask.id,
                      handleErrors,
                    )
                    return updatedTask
                  },
                })
              }
            }}
          />
        </div>
      </div>

      {activeChips.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {activeChips.map((chip) => (
            <span
              key={chip.id}
              className="bg-brownGray-100 text-charcoal-900 inline-flex items-center gap-1 rounded-full py-1 pl-3 pr-2 text-xs font-medium"
            >
              {chip.text}
              <button
                type="button"
                onClick={chip.remove}
                aria-label={`Remove ${chip.text} filter`}
                className="text-charcoal-400 hover:text-charcoal-900"
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={() => {
              setSelectedResolvedFilters(DEFAULT_FILTERS)
              setResolvedDateFilters(DEFAULT_RESOLVED_DATE_FILTERS)
            }}
            className="text-xs font-semibold text-blue-800 hover:text-blue-900"
          >
            Clear all
          </button>
        </div>
      )}

      <DataTable
        fillViewport
        fillViewportWatch={tableLayoutWatch}
        onRowClicked={(row) =>
          navigate(`/dealer/${row.dealer}/repair-order/${row.id}`, { state: { dealer } })
        }
        highlightOnHover
        columns={updatedResolvedColumns}
        data={resolvedRepairOrders}
        {...getSortProps(resolvedSortedColumn, updatedResolvedColumns)}
        expandableIcon={{
          collapsed: (
            <div className="flex flex-row gap-2">
              <div className="bg-charcoal-light h-[38px] w-2 rounded-r-[4px]" />
              <AddIcon className="stroke-charcoal-light h-4" />
            </div>
          ),
          expanded: (
            <div className="flex flex-row gap-3">
              <div className="bg-gold-dark h-[38px] w-2 rounded-r-[4px]" />
              <MinusIcon className="stroke-charcoal-light h-4" />
            </div>
          ),
        }}
        expandableRows
        expandableRowsComponent={resolvedExpandableRow}
        onChangePage={(page) =>
          handlePagination(
            page,
            resolvedCurrentPage,
            resolvedPerPage,
            resolvedTotalRows,
            resolvedPages,
            setResolvedCurrentPage,
            (url) => getUpdatedRepairOrders(url),
            BASE_URL,
            `order_by=${resolvedSortedColumn}${urlParams === '' ? '' : `&${urlParams}`}`,
          )
        }
        onChangeRowsPerPage={(currentRowsPerPage) => setResolvedPerPage(currentRowsPerPage)}
        onSort={(column, direction) => {
          track(SORT_CHANGED, {
            context: 'dealer_detail_resolved',
            column: column.sortBy,
            direction,
          })
          const d = direction === 'asc' ? '' : '-'
          setResolvedSortedColumn(`${d}${column.sortBy}`)
        }}
        pagination
        paginationServer
        paginationDefaultPage={resolvedCurrentPage}
        paginationPerPage={resolvedPerPage}
        paginationRowsPerPageOptions={[10, 20, 30, 50]}
        paginationTotalRows={resolvedTotalRows}
        progressPending={loadingResolved}
        sortServer
      />
    </div>
  )
}

ResolvedROs.propTypes = {
  // Whether the Resolved tab is the one showing.
  active: PropTypes.bool,
  dealer: PropTypes.object,
  dealerId: PropTypes.string.isRequired,
  dealerName: PropTypes.string.isRequired,
  // Called with the number of resolved ROs, for the tab's count.
  onCountChange: PropTypes.func,
  // Values that move the table on the page, for its fill-the-screen height.
  tableLayoutWatch: PropTypes.array,
}

ResolvedROs.defaultProps = {
  active: false,
  dealer: null,
  onCountChange: () => {},
  tableLayoutWatch: [],
}
